import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../utils/api';
import ExcelImportPanel from './ExcelImportPanel';
import * as XLSX from 'xlsx';

const FACILITY_MAX = 65535;
const ACCESS_MAX = 16777215;
const PAGE_SIZE = 8; // used for both the date-filtered view and the default month view

// ── Country codes with national phone-number length (used to cap input) ────
const COUNTRY_CODES = [
  { iso: 'IN', name: 'India', dial: '+91', length: 10 },
  { iso: 'US', name: 'USA', dial: '+1', length: 10 },
  { iso: 'GB', name: 'UK', dial: '+44', length: 10 },
  { iso: 'AE', name: 'UAE', dial: '+971', length: 9 },
  { iso: 'SG', name: 'Singapore', dial: '+65', length: 8 },
  { iso: 'AU', name: 'Australia', dial: '+61', length: 9 },
  { iso: 'CA', name: 'Canada', dial: '+1', length: 10 },
  { iso: 'DE', name: 'Germany', dial: '+49', length: 11 },
  { iso: 'FR', name: 'France', dial: '+33', length: 9 },
  { iso: 'SA', name: 'Saudi Arabia', dial: '+966', length: 9 },
  { iso: 'MY', name: 'Malaysia', dial: '+60', length: 9 },
  { iso: 'NZ', name: 'New Zealand', dial: '+64', length: 9 },
];

// ── Datetime-local helpers ────────────────────────────────────────────────────
const toDatetimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const DURATION_PRESETS = [
  { key: '1d', label: '1 Day', ms: 1 * 24 * 60 * 60 * 1000 },
  { key: '3d', label: '3 Days', ms: 3 * 24 * 60 * 60 * 1000 },
  { key: '7d', label: '7 Days', ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '30d', label: '30 Days', ms: 30 * 24 * 60 * 60 * 1000 },
];

// ── SVG Icons ────────────────────────────────────────────────────────────────
const IconKey = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6" />
    <path d="M15.5 7.5l3 3L22 7l-3-3" />
  </svg>
);
const GearIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const WarnIcon = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
const CalendarIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
const MailIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16v12H4z" /><path d="M4 7l8 6 8-6" /></svg>;
const EditIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>;
const UserPlusIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>;
const BlockUserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="17" y1="8" x2="22" y2="13" />
    <line x1="22" y1="8" x2="17" y2="13" />
  </svg>
);
const PowerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

const BLOCK_LABELS = { resigned: 'Resigned', blocklisted: 'Blocklisted' };

// Popup text when someone tries to add a resigned / blocklisted person (field: 'email' | 'codes')
const blockedAlertFor = (type, field) => {
  const label = (BLOCK_LABELS[type] || 'Blocklisted').toLowerCase();
  return {
    title: `${BLOCK_LABELS[type] || 'Blocklisted'} Employee`,
    message: field === 'email'
      ? `The email address you entered belongs to an employee who is ${label}. Please use a different email address.`
      : `The Facility Code and Access Code combination you entered belongs to an employee who is ${label}. Please use a different Facility Code and Access Code.`,
  };
};

// ── Avatar initials + a stable color per person, based on their name/email ──
const AVATAR_COLORS = ['#1976d2', '#7c3aed', '#059669', '#d97706', '#db2777', '#0891b2', '#4f46e5', '#0d9488'];
const avatarColorFor = (key) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const initialsFor = (t) => {
  const fromName = `${(t.firstName || '').charAt(0)}${(t.lastName || '').charAt(0)}`.toUpperCase();
  return fromName || (t.employeeEmail || '?').charAt(0).toUpperCase();
};

// ── Token validity helper — checked against validUntil directly rather than
//    relying on isActive, since isActive only flips server-side once /verify
//    has been called on that token at least once after expiry.
const isTokenExpired = (t) => !!t.validUntil && new Date(t.validUntil) < new Date();

// ── Generic duplicate/conflict alert modal ──────────────────────────────────
const DuplicateAlertModal = ({ title, message, onClose }) => (
  <div className="modal-backdrop">
    <div className="modal-box">
      <div className="modal-icon-wrap modal-icon-wrap--orange">
        <WarnIcon />
      </div>
      <h3 className="modal-title">{title}</h3>
      <p className="modal-desc">{message}</p>
      <div className="modal-actions">
        <button className="modal-btn modal-btn--orange" onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  </div>
);

// ── Deactivate-card confirm modal ───────────────────────────────────────────
const DeactivateConfirmModal = ({ onConfirm, onCancel, loading }) => (
  <div className="modal-backdrop">
    <div className="modal-box">
      <div className="modal-icon-wrap modal-icon-wrap--orange">
        <WarnIcon />
      </div>
      <h3 className="modal-title">Deactivate this card?</h3>
      <p className="modal-desc">
        Are you sure you want to deactivate this user's card? To add it on a new device, the user will need to sign in again from the mobile app (Get OTP).
      </p>
      <div className="modal-actions">
        <button className="modal-btn modal-btn--cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button className="modal-btn modal-btn--orange" onClick={onConfirm} disabled={loading}>
          {loading ? <><span className="tg-spinner" style={{ width: 13, height: 13 }} /> Deactivating...</> : 'Yes, deactivate'}
        </button>
      </div>
    </div>
  </div>
);

// ── Resigned / Blocklist dialog ─────────────────────────────────────────────
const BlockUserModal = ({ user, onConfirm, onCancel, loading, error }) => {
  const [type, setType] = useState('resigned');
  const [reason, setReason] = useState('');
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.employeeEmail;
  return (
    <div className="modal-backdrop">
      <div className="modal-box">
        <div className="modal-icon-wrap modal-icon-wrap--red">
          <WarnIcon />
        </div>
        <h3 className="modal-title">Mark {name} as Resigned / Blocklisted?</h3>
        <p className="modal-desc">
          The card will be switched off on every device, and this user's email, facility code and access code will be saved in the blocklist. They will not be able to sign in from the mobile app.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '0 0 12px' }}>
          {Object.entries(BLOCK_LABELS).map(([key, label]) => (
            <button
              type="button"
              key={key}
              className={`tg-duration-btn ${type === key ? 'tg-duration-btn--active' : ''}`}
              onClick={() => setType(key)}
              disabled={loading}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="text"
          className="tg-input"
          placeholder="Reason (optional)"
          maxLength={300}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={loading}
          style={{ marginBottom: 12 }}
        />
        {error && <p className="tg-error" style={{ marginBottom: 10 }}>{error}</p>}
        <div className="modal-actions">
          <button className="modal-btn modal-btn--cancel" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="modal-btn modal-btn--red" onClick={() => onConfirm(type, reason)} disabled={loading}>
            {loading
              ? <><span className="tg-spinner" style={{ width: 13, height: 13 }} /> Saving...</>
              : `Mark as ${BLOCK_LABELS[type]}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Quota Bar ─────────────────────────────────────────────────────────────────
const QuotaBar = ({ used, limit }) => {
  if (limit === null || limit === undefined) return null; // unlimited — hide entirely

  const remaining = Math.max(0, limit - used);
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isOut = remaining === 0;
  const isLow = !isOut && remaining <= Math.max(1, Math.floor(limit * 0.2)); // ≤20% left
  const barColor = isOut ? '#ef4444' : isLow ? '#f59e0b' : '#1976d2';
  const bgColor = isOut ? '#fef2f2' : isLow ? '#fffbeb' : '#e3f2fd';
  const borderColor = isOut ? '#fecaca' : isLow ? '#fde68a' : '#90caf9';
  const textColor = isOut ? '#b91c1c' : isLow ? '#92400e' : '#1565c0';

  return (
    <div style={{
      background: bgColor, border: `1px solid ${borderColor}`,
      borderRadius: 10, padding: '14px 16px', marginBottom: '1.25rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: textColor }}>
          {isOut ? 'Token Limit Reached' : isLow ? 'Token Limit Low' : 'Token Quota'}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 6, background: '#e5e7eb', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 6,
          width: `${pct}%`,
          background: isOut ? '#ef4444' : `linear-gradient(90deg, ${barColor}88, ${barColor})`,
          transition: 'width 0.4s ease',
        }} />
      </div>
      {isOut && (
        <p style={{ fontSize: '0.75rem', color: textColor, margin: '8px 0 0', lineHeight: 1.5 }}>
          You have used all tokens. Please contact your <strong>vendor or admin</strong> to request an increase.
        </p>
      )}
      {isLow && !isOut && (
        <p style={{ fontSize: '0.75rem', color: textColor, margin: '8px 0 0' }}>
          Only <strong>{remaining}</strong> token{remaining !== 1 ? 's' : ''} left. Contact your admin if you need more.
        </p>
      )}
    </div>
  );
};

// ── Limit Reached Banner ──────────────────────────────────────────────────────
const LimitReachedBanner = ({ role }) => {
  const contactWho = role?.toLowerCase() === 'user' ? 'vendor or admin' : 'admin';
  return (
    <div style={{
      background: '#fef2f2', border: '1.5px solid #fecaca',
      borderRadius: 10, padding: '16px 18px', marginTop: '1rem',
      display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <div>
        <p style={{ margin: 0, fontWeight: 700, fontSize: '0.875rem', color: '#b91c1c' }}>
          Token Limit Reached
        </p>
        <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#991b1b', lineHeight: 1.6 }}>
          You have used all your allocated tokens. Please contact your{' '}
          <strong>{contactWho}</strong> to request additional tokens.
        </p>
      </div>
    </div>
  );
};

// ── Card details panel (the gear icon view) ─────────────────────────────────
const CardDetailsPanel = ({ card, deactivating, deactivateError, justDeactivated, onDeactivate, onBlock, onClose }) => {
  const isBlocked = !!card.blockStatus;
  const isActive = !!card.claimId && !isBlocked;
  const fullName = `${card.firstName || ''} ${card.lastName || ''}`.trim() || card.employeeEmail;

  const chip = isBlocked
    ? { text: ` ${BLOCK_LABELS[card.blockStatus]}`, bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' }
    : isActive
      ? { text: '● Active', bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' }
      : { text: '○ Inactive', bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb' };

  const fmt = (d) => (d
    ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—');

  const rows = [
    ['Employee ID', card.employeeId || '—'],
    ['Client', card.clientName || '—'],
    ['Facility Code', card.facilityCode ?? '—'],
    ['Access Code', card.accessCode ?? '—'],
    ['Valid From', fmt(card.validFrom)],
    ['Valid To', fmt(card.validUntil)],
    ['Device', isActive ? (card.claimedByDevice || 'Unknown') : 'None'],
    ['Phone', card.phone || '—'],
  ];

  const note = (bg, border, color, children) => (
    <div style={{ background: bg, border: `1px solid ${border}`, color, borderRadius: 10, padding: '10px 12px', fontSize: '0.8rem', lineHeight: 1.5, marginBottom: 10 }}>
      {children}
    </div>
  );

  return (
    <div style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 14, background: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.06)', overflow: 'hidden' }}>

      {/* Header: avatar, name, email, status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'linear-gradient(135deg,#eff6ff,#f8fafc)', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: avatarColorFor(card.employeeEmail || card._id || 'x'), color: '#fff', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {initialsFor(card)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 700, color: '#111827', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fullName}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <MailIcon /> {card.employeeEmail}
          </p>
        </div>
        <span style={{
          padding: '4px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
          background: chip.bg, color: chip.color, border: `1px solid ${chip.border}`,
        }}>
          {chip.text}
        </span>
      </div>

      {/* Details grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', padding: '14px 16px' }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af' }}>{label}</p>
            <p style={{ margin: '2px 0 0', fontSize: '0.84rem', fontWeight: 600, color: '#111827', wordBreak: 'break-word' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Status messages */}
      <div style={{ padding: '0 16px' }}>
        {isBlocked && note('#fef2f2', '#fecaca', '#b91c1c',
          <> This user is <strong>{BLOCK_LABELS[card.blockStatus]}</strong>. They can't sign in from the mobile app.</>)}

        {!isBlocked && !isActive && note('#eff6ff', '#bfdbfe', '#1e40af',
          <>
            {justDeactivated && <><strong>✓ Card set to inactive.</strong><br /></>}
            This card is inactive. Please use the user ID (<strong>{card.employeeEmail}</strong>) in the mobile app to use this card.
          </>)}

        {deactivateError && <p className="tg-error" style={{ marginBottom: 10 }}>{deactivateError}</p>}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '4px 16px 16px' }}>
        <button
          type="button"
          className="tg-hbtn tg-hbtn--warn"
          onClick={onDeactivate}
          disabled={!isActive || deactivating}
          title={isActive ? 'Set this card to inactive' : 'This card is already inactive'}
        >
          <PowerIcon /> {deactivating ? 'Updating...' : 'Inactive'}
        </button>

        {!isBlocked && (
          <button type="button" className="tg-hbtn tg-hbtn--danger" onClick={onBlock}>
            <BlockUserIcon /> Resigned
          </button>
        )}

        <button type="button" className="tg-hbtn tg-hbtn--success" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

// ── Resigned / Blocklisted list (opened from the history header) ────────────
const BlocklistPanel = ({ users, loading, tab, onTabChange, query }) => {
  const q = query.trim().toLowerCase();
  const matches = (u) => {
    if (!q) return true;
    const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    return name.includes(q) || (u.employeeId || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  };
  const counts = {
    resigned: users.filter((u) => u.type === 'resigned').length,
    blocklisted: users.filter((u) => u.type === 'blocklisted').length,
  };
  const shown = users.filter((u) => u.type === tab).filter(matches);

  return (
    <>
      <div style={{ display: 'flex', gap: 8, margin: '4px 0 14px' }}>
        {Object.entries(BLOCK_LABELS).map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={`tg-duration-btn ${tab === key ? 'tg-duration-btn--active' : ''}`}
            onClick={() => onTabChange(key)}
          >
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="tg-history-empty"><p>Loading...</p></div>
      ) : shown.length === 0 ? (
        <div className="tg-history-empty">
          <p>No {BLOCK_LABELS[tab].toLowerCase()} users{q ? ' match your search' : ' yet'}.</p>
        </div>
      ) : (
        <div className="tg-history-list">
          {shown.map((u) => {
            const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
            return (
              <div className="tg-history-item" key={u._id}>
                <div className="tg-history-row">
                  <div className="tg-history-email-wrap">
                    <div className="tg-history-avatar" style={{ background: avatarColorFor(u.email || 'x'), color: '#fff', fontWeight: 700 }}>
                      {initialsFor({ firstName: u.firstName, lastName: u.lastName, employeeEmail: u.email })}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p className="tg-history-email" style={{ margin: 0, fontWeight: 600, color: '#111827' }}>{name}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <MailIcon /> {u.email}
                      </p>
                      <p className="tg-history-meta" style={{ marginTop: 4 }}>
                        {u.clientName} &nbsp;·&nbsp; ID: {u.employeeId} &nbsp;·&nbsp; FC: {u.facilityCode} &nbsp;·&nbsp; AC: {u.accessCode}
                      </p>
                      {u.reason && (
                        <p style={{ margin: '4px 0 0', fontSize: '0.76rem', color: '#6b7280' }}>Reason: {u.reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="tg-history-right">
                    <span className="tg-history-badge tg-history-badge--blocked"><BlockUserIcon /> {BLOCK_LABELS[u.type]}</span>
                    <p className="tg-history-date">
                      {u.blockedAt
                        ? new Date(u.blockedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : ''}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// ── Pagination controls ─────────────────────────────────────────────────────
const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const windowSize = 1;
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - windowSize && i <= currentPage + windowSize)
    ) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div className="tg-pagination">
      <button
        className="tg-page-btn"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ‹ Prev
      </button>

      {pages.map((p, idx) =>
        p === '...' ? (
          <span key={`ellipsis-${idx}`} className="tg-page-ellipsis">…</span>
        ) : (
          <button
            key={p}
            className={`tg-page-btn ${p === currentPage ? 'tg-page-btn--active' : ''}`}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        )
      )}

      <button
        className="tg-page-btn"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next ›
      </button>
    </div>
  );
};

// ── Extend-validity inline panel — shown under a row when its edit button
//    is clicked. Defaults the two fields to "now" / "now + 1 day".
const ExtendValidityPanel = ({ token, onSubmit, onCancel, loading, error }) => {
  const nowLocal = new Date();
  const [validFrom, setValidFrom] = useState(toDatetimeLocal(nowLocal));
  const [validUntil, setValidUntil] = useState(toDatetimeLocal(new Date(nowLocal.getTime() + DURATION_PRESETS[0].ms)));

  return (
    <div className="tg-extend-panel" style={{ marginTop: 10, padding: 14, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10 }}>
      <p style={{ margin: '0 0 10px', fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
        This token's validity ended on {new Date(token.validUntil).toLocaleString('en-IN')}. Extend it:
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div className="tg-field" style={{ flex: '1 1 180px', margin: 0 }}>
          <label className="tg-label">Valid From</label>
          <input type="datetime-local" className="tg-input" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="tg-field" style={{ flex: '1 1 180px', margin: 0 }}>
          <label className="tg-label">Valid To</label>
          <input type="datetime-local" className="tg-input" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </div>
      </div>
      {error && <p className="tg-error" style={{ marginTop: 8 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          className="tg-btn"
          style={{ padding: '8px 16px', fontSize: '0.82rem', width: 'auto' }}
          disabled={loading}
          onClick={() => onSubmit(validFrom, validUntil)}
        >
          {loading ? <><span className="tg-spinner" style={{ width: 13, height: 13 }} /> Saving...</> : 'Save'}
        </button>
        <button type="button" className="tg-hbtn tg-hbtn--neutral" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Single history row (no delete action in this view) ─────────────────────
const HistoryRow = ({ token, onView, isEditing, onEditClick, onExtendSubmit, onExtendCancel, extending, extendError }) => {
  const expired = isTokenExpired(token);
  return (
    <div className="tg-history-item">
      <div className="tg-history-row">

        {/* Left: avatar + info */}
        <div className="tg-history-email-wrap">
          <div className="tg-history-avatar" style={{ background: avatarColorFor(token.employeeEmail || token._id || 'x'), color: '#fff', fontWeight: 700 }}>
            {initialsFor(token)}
          </div>
          <div style={{ minWidth: 0 }}>
            <p className="tg-history-email" style={{ margin: 0, fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
              {(token.firstName || token.lastName) ? `${token.firstName || ''} ${token.lastName || ''}`.trim() : token.employeeEmail}
              {expired && !token.blockStatus && (
                <button
                  type="button"
                  className="tg-edit-validity-btn"
                  onClick={onEditClick}
                  title="Extend validity"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 22, height: 22, borderRadius: 6, border: '1px solid #fbbf24',
                    background: '#fffbeb', color: '#b45309', cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <EditIcon />
                </button>
              )}
            </p>
            {(token.firstName || token.lastName) && (
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                <MailIcon /> {token.employeeEmail}
              </p>
            )}
            <p className="tg-history-meta" style={{ marginTop: 4 }}>
              {token.clientName} &nbsp;·&nbsp; ID: {token.employeeId} &nbsp;·&nbsp; FC: {token.facilityCode} &nbsp;·&nbsp; AC: {token.accessCode}
              {token.phone && <> &nbsp;·&nbsp; {token.phone}</>}
            </p>
          </div>
        </div>

        {/* Right: date + badge + manage */}
        <div className="tg-history-right">
          <span className={token.blockStatus ? 'tg-history-badge tg-history-badge--blocked' : expired ? 'tg-history-badge tg-history-badge--expired' : 'tg-history-badge'}>
            {token.blockStatus
              ? <><BlockUserIcon /> {BLOCK_LABELS[token.blockStatus]}</>
              : expired ? '⚠ validity ended' : '✓ saved'}
          </span>
          <p className="tg-history-date">
            {token.sentAt
              ? new Date(token.sentAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
              : 'Just now'}
          </p>
          <button
            className="tg-delete-row-btn"
            onClick={onView}
            title="View / manage this card"
            style={{ background: '#eff6ff', color: '#1d4ed8' }}
          >
            <GearIcon />
          </button>
        </div>

      </div>

      {isEditing && (
        <ExtendValidityPanel
          token={token}
          onSubmit={(validFrom, validUntil) => onExtendSubmit(token._id, validFrom, validUntil)}
          onCancel={onExtendCancel}
          loading={extending}
          error={extendError}
        />
      )}
    </div>
  );
};

// ── Custom calendar dropdown (renders identically on every browser) ─────────
const CalendarDropdown = ({ selectedDate, onSelect, onClear, onClose }) => {
  const [viewMonth, setViewMonth] = useState(
    selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date()
  );
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const toLocalISODate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const getCalendarDays = (viewDate) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const startWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const days = [];
    for (let i = startWeekday - 1; i >= 0; i--)
      days.push({ day: daysInPrevMonth - i, current: false, date: new Date(year, month - 1, daysInPrevMonth - i) });
    for (let d = 1; d <= daysInMonth; d++)
      days.push({ day: d, current: true, date: new Date(year, month, d) });
    while (days.length < 42) {
      const idx = days.length - (startWeekday + daysInMonth) + 1;
      days.push({ day: idx, current: false, date: new Date(year, month + 1, idx) });
    }
    return days;
  };

  const today = new Date();
  const prevMonth = () => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  return (
    <div className="tg-calendar-popover" ref={wrapRef}>
      <div className="tg-calendar-header">
        <button type="button" className="tg-calendar-nav" onClick={prevMonth}>‹</button>
        <span className="tg-calendar-month-label">
          {viewMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
        </span>
        <button type="button" className="tg-calendar-nav" onClick={nextMonth}>›</button>
      </div>
      <div className="tg-calendar-weekdays">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="tg-calendar-grid">
        {getCalendarDays(viewMonth).map(({ day, current, date }, idx) => {
          const isoDate = toLocalISODate(date);
          const isSelected = selectedDate === isoDate;
          const isToday = isoDate === toLocalISODate(today);
          return (
            <button
              type="button"
              key={idx}
              className={`tg-calendar-day ${current ? '' : 'tg-calendar-day--muted'} ${isSelected ? 'tg-calendar-day--selected' : ''} ${isToday && !isSelected ? 'tg-calendar-day--today' : ''}`}
              onClick={() => onSelect(isoDate)}
            >
              {day}
            </button>
          );
        })}
      </div>
      <div className="tg-calendar-footer">
        <button type="button" className="tg-calendar-link" onClick={onClear}>Clear</button>
        <button
          type="button"
          className="tg-calendar-link"
          onClick={() => { setViewMonth(today); onSelect(toLocalISODate(today)); }}
        >
          Today
        </button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const TokenPage = ({ apiBase, accentColor = '#e3f2fd', role = 'Admin' }) => {
  const now = new Date();
  const [form, setForm] = useState({
    clientName: '',
    facilityCode: '',
    accessCode: '',
    employeeId: '',
    firstName: '',
    lastName: '',
    employeeEmail: '',
    countryCode: '+91',
    phoneNumber: '',
    validFrom: toDatetimeLocal(now),
    validUntil: toDatetimeLocal(new Date(now.getTime() + DURATION_PRESETS[0].ms)),
  });
  const [durationPreset, setDurationPreset] = useState('1d');
  const [errors, setErrors] = useState({});
  const [successEmail, setSuccessEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);

  // Card view / deactivate / block state
  const [viewingCard, setViewingCard] = useState(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  const [deactivateSuccess, setDeactivateSuccess] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [blockError, setBlockError] = useState('');

  // Search bar
  const [searchQuery, setSearchQuery] = useState('');

  // Duplicate alert — null | { title, message }
  const [duplicateAlert, setDuplicateAlert] = useState(null);

  // Extend-validity state
  const [editingTokenId, setEditingTokenId] = useState(null);
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState('');

  // Date filter + pagination
  const [selectedDate, setSelectedDate] = useState('');
  const [page, setPage] = useState(1);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Resigned / Blocklisted list
  const [blocklist, setBlocklist] = useState([]);
  const [blocklistLoading, setBlocklistLoading] = useState(false);
  const [showBlocklist, setShowBlocklist] = useState(false);
  const [blockTab, setBlockTab] = useState('resigned');

  // Quota state — only relevant for vendor/user (not admin)
  const [quota, setQuota] = useState({ tokenLimit: null, tokensUsed: 0 });

  // ── Fetch quota ─────────────────────────────────────────────────────────────
  const fetchQuota = useCallback(async () => {
    if (role === 'Admin') return; // admin has no limit
    try {
      const res = await API.get(`${apiBase}/quota`);
      setQuota({
        tokenLimit: res.data.tokenLimit,
        tokensUsed: res.data.tokensUsed,
      });
    } catch {
      // quota endpoint missing or error — silently ignore
    }
  }, [apiBase, role]);

  // ── Fetch history ───────────────────────────────────────────────────────────
  const loadHistory = useCallback(() => {
    return API.get(`${apiBase}/history`)
      .then((res) => {
        const tokens = (res.data.tokens || []).map((t) => ({
          ...t,
          _id: t._id?.toString() || t._id,
        }));
        setHistory(tokens);
      });
  }, [apiBase]);

  // ── Fetch blocklist ─────────────────────────────────────────────────────────
  const loadBlocklist = useCallback(() => {
    setBlocklistLoading(true);
    API.get(`${apiBase}/blocklist`)
      .then((res) => setBlocklist(res.data.users || []))
      .catch(() => setBlocklist([]))
      .finally(() => setBlocklistLoading(false));
  }, [apiBase]);

  useEffect(() => {
    loadHistory()
      .catch(() => setHistory([]))
      .finally(() => setHistLoading(false));
    loadBlocklist();
    fetchQuota();
  }, [loadHistory, loadBlocklist, fetchQuota]);

  // ── Auto-refresh every minute (no full page reload) ─────────────────────────
  // 1) tick: re-renders so "validity ended" badges and the extend button appear
  //    as soon as a token's validUntil passes (expiry is computed from the clock).
  // 2) poll: re-fetches history/quota/blocklist so changes made elsewhere show up.
  //    Skipped while the browser tab is hidden.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
      if (document.hidden) return;
      loadHistory().catch(() => {}); // keep the current list if a refresh fails
      loadBlocklist();
      fetchQuota();
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [loadHistory, loadBlocklist, fetchQuota]);

  const openBlocklist = () => {
    setBlockTab('resigned');
    setSearchQuery('');
    setShowBlocklist(true);
    loadBlocklist();
  };

  // Reset card-panel messages whenever a different card is opened
  useEffect(() => {
    if (!viewingCard) return;
    setDeactivateError('');
    setDeactivateSuccess(false);
  }, [viewingCard]);

  // ── Form handlers ───────────────────────────────────────────────────────────
  const clearBanners = () => { setSuccessEmail(''); setErrorMsg(''); setLimitReached(false); };

  const handleNumberInput = (e) => {
    const { name, value } = e.target;
    if (!/^\d*$/.test(value)) return;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: '' }));
    clearBanners();
  };

  const handleTextInput = (e) => {
    const { name, value } = e.target;
    // Block leading spaces — stops a field from being "filled" with just whitespace
    const cleaned = value.replace(/^\s+/, '');
    setForm((f) => ({ ...f, [name]: cleaned }));
    setErrors((er) => ({ ...er, [name]: '' }));
    clearBanners();
  };

  const handleEmailInput = (e) => {
    const cleaned = e.target.value.replace(/^\s+/, '');
    setForm((f) => ({ ...f, employeeEmail: cleaned }));
    setErrors((er) => ({ ...er, employeeEmail: '' }));
    clearBanners();
  };

  // Phone is digits-only and capped to the selected country's national length
  const handlePhoneInput = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    const country = COUNTRY_CODES.find((c) => c.dial === form.countryCode);
    const capped = digitsOnly.slice(0, country?.length || 15);
    setForm((f) => ({ ...f, phoneNumber: capped }));
    setErrors((er) => ({ ...er, phoneNumber: '' }));
    clearBanners();
  };

  const handleCountryCodeChange = (e) => {
    const dial = e.target.value;
    // Clear the number on switch — a number valid under one country's length rule
    // is meaningless under another's.
    setForm((f) => ({ ...f, countryCode: dial, phoneNumber: '' }));
    setErrors((er) => ({ ...er, phoneNumber: '' }));
  };

  // ── Validity handlers ────────────────────────────────────────────────────────
  const handleValidFromChange = (e) => {
    const value = e.target.value;
    setForm((f) => {
      if (durationPreset === 'custom' || !value) return { ...f, validFrom: value };
      const preset = DURATION_PRESETS.find((p) => p.key === durationPreset);
      const fromDate = new Date(value);
      const untilDate = new Date(fromDate.getTime() + preset.ms);
      return { ...f, validFrom: value, validUntil: toDatetimeLocal(untilDate) };
    });
    setErrors((er) => ({ ...er, validFrom: '', validUntil: '' }));
  };

  const handleDurationPreset = (key) => {
    setDurationPreset(key);
    if (key === 'custom') return;
    const preset = DURATION_PRESETS.find((p) => p.key === key);
    const fromDate = form.validFrom ? new Date(form.validFrom) : new Date();
    const untilDate = new Date(fromDate.getTime() + preset.ms);
    setForm((f) => ({ ...f, validUntil: toDatetimeLocal(untilDate) }));
    setErrors((er) => ({ ...er, validUntil: '' }));
  };

  const handleValidUntilChange = (e) => {
    setDurationPreset('custom');
    setForm((f) => ({ ...f, validUntil: e.target.value }));
    setErrors((er) => ({ ...er, validUntil: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.clientName.trim())
      errs.clientName = 'Client name is required.';
    if (!form.facilityCode.trim())
      errs.facilityCode = 'Facility code is required.';
    else if (parseInt(form.facilityCode, 10) > FACILITY_MAX)
      errs.facilityCode = `Facility code cannot exceed ${FACILITY_MAX.toLocaleString()} (16-bit limit).`;
    if (!form.accessCode.trim())
      errs.accessCode = 'Access code is required.';
    else if (parseInt(form.accessCode, 10) > ACCESS_MAX)
      errs.accessCode = `Access code cannot exceed ${ACCESS_MAX.toLocaleString()} (24-bit limit).`;
    if (!form.employeeId.trim())
      errs.employeeId = 'Employee ID is required.';
    if (!form.firstName.trim())
      errs.firstName = 'First name is required.';
    if (!form.lastName.trim())
      errs.lastName = 'Last name is required.';
    if (!form.employeeEmail.trim())
      errs.employeeEmail = 'Employee email is required.';
    else if (!/^\S+@\S+\.\S+$/.test(form.employeeEmail.trim()))
      errs.employeeEmail = 'Enter a valid email address.';

    // Phone is optional — only validate length if the user typed something
    if (form.phoneNumber.trim()) {
      const country = COUNTRY_CODES.find((c) => c.dial === form.countryCode);
      if (country && form.phoneNumber.length !== country.length) {
        errs.phoneNumber = `Enter a valid ${country.length}-digit number for ${country.name}.`;
      }
    }

    if (!form.validFrom)
      errs.validFrom = '"Valid From" is required.';
    if (!form.validUntil)
      errs.validUntil = '"Valid To" is required.';
    else if (form.validFrom && new Date(form.validUntil) <= new Date(form.validFrom))
      errs.validUntil = '"Valid To" must be after "Valid From".';
    return errs;
  };

  // ── Client-side duplicate guards (UX only — the backend must enforce the same
  //    rules and return 409, since these can be bypassed or raced) ────────────
  const isDuplicateCode = (facilityNum, accessNum) =>
    history.some(
      (t) => Number(t.facilityCode) === facilityNum && Number(t.accessCode) === accessNum
    );

  const isDuplicateEmail = (email) =>
    history.some(
      (t) => (t.employeeEmail || '').trim().toLowerCase() === email.trim().toLowerCase()
    );

  const isDuplicateEmployeeId = (employeeId) =>
    history.some(
      (t) => (t.employeeId || '').trim().toLowerCase() === employeeId.trim().toLowerCase()
    );

  // ── Export full token history to Excel ──────────────────────────────────────
  const handleExportHistory = () => {
    if (history.length === 0) return;

    const rows = history.map((t) => ({
      'Client Name': t.clientName,
      'Employee ID': t.employeeId,
      'Facility Code': t.facilityCode,
      'Access Code': t.accessCode,
      'First Name': t.firstName,
      'Last Name': t.lastName,
      'Employee Email': t.employeeEmail,
      'Phone': t.phone,
      'Status': t.status,
      'Block Status': t.blockStatus ? BLOCK_LABELS[t.blockStatus] : '',
      'Saved At': t.sentAt ? new Date(t.sentAt).toLocaleString('en-IN') : '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map(() => ({ wch: 20 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Token History');
    XLSX.writeFile(wb, `token_history_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Save user ───────────────────────────────────────────────────────────────
  // Only stores the user's details. No email and no OTP are sent from here —
  // the user requests their OTP from the mobile app (Get OTP).
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setErrorMsg('Please fill in all required fields to continue.');
      return;
    }

    // Trim once here so saved data never carries stray leading/trailing spaces
    const clientName = form.clientName.trim();
    const employeeId = form.employeeId.trim();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const employeeEmail = form.employeeEmail.trim();
    const phoneNumber = form.phoneNumber.trim();
    const facilityNum = parseInt(form.facilityCode, 10);
    const accessNum = parseInt(form.accessCode, 10);

    // Resigned / blocklisted person? Show that specific popup instead of the generic duplicate one.
    const blockedMatch = blocklist.find((b) =>
      b.email === employeeEmail.toLowerCase() ||
      (Number(b.facilityCode) === facilityNum && Number(b.accessCode) === accessNum)
    );
    if (blockedMatch) {
      setDuplicateAlert(blockedAlertFor(
        blockedMatch.type,
        blockedMatch.email === employeeEmail.toLowerCase() ? 'email' : 'codes'
      ));
      return;
    }

    if (isDuplicateEmail(employeeEmail)) {
      setDuplicateAlert({
        title: 'Email Already Used',
        message: 'A token has already been generated with this email address. Please try with a different email.',
      });
      return;
    }

    if (isDuplicateEmployeeId(employeeId)) {
      setDuplicateAlert({
        title: 'Employee ID Already Used',
        message: 'A token has already been generated with this employee ID. Please try with a different employee ID.',
      });
      return;
    }

    if (isDuplicateCode(facilityNum, accessNum)) {
      setDuplicateAlert({
        title: 'Facility / Access Code Already in Use',
        message: 'A token with this Facility Code and Access Code combination has already been generated. Please use a different Facility Code or Access Code.',
      });
      return;
    }

    setLoading(true);
    clearBanners();

    try {
      const res = await API.post(`${apiBase}/save`, {
        clientName,
        facilityCode: facilityNum,
        accessCode: accessNum,
        employeeId,
        firstName,
        lastName,
        employeeEmail,
        phone: phoneNumber ? `${form.countryCode}${phoneNumber}` : null,
        validFrom: new Date(form.validFrom).toISOString(),
        validUntil: new Date(form.validUntil).toISOString(),
      });

      setSuccessEmail(employeeEmail);

      // Add to top of history with _id from server
      setHistory((h) => [{
        _id: res.data._id || Date.now().toString(),
        clientName,
        token: res.data.tokenId,
        facilityCode: facilityNum,
        accessCode: accessNum,
        employeeId,
        firstName,
        lastName,
        employeeEmail,
        phone: phoneNumber ? `${form.countryCode}${phoneNumber}` : '',
        validFrom: res.data.validFrom,
        validUntil: res.data.validUntil,
        sentAt: new Date().toISOString(),
        status: 'sent',
      }, ...h].slice(0, 50));

      // New user lands at the top — jump back to page 1 so it is visible
      setPage(1);

      const resetNow = new Date();
      setDurationPreset('1d');
      setForm({
        clientName: '',
        facilityCode: '',
        accessCode: '',
        employeeId: '',
        firstName: '',
        lastName: '',
        employeeEmail: '',
        countryCode: '+91',
        phoneNumber: '',
        validFrom: toDatetimeLocal(resetNow),
        validUntil: toDatetimeLocal(new Date(resetNow.getTime() + DURATION_PRESETS[0].ms)),
      });

      // Refresh quota after a successful save (no-op for Admin role)
      await fetchQuota();

    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.blocked) {
        setDuplicateAlert(blockedAlertFor(err.response.data.blockType, err.response.data.blockedField));
        loadBlocklist();
      } else if (err.response?.status === 409) {
        const msg = (err.response?.data?.message || '').toLowerCase();
        if (msg.includes('employee id') || msg.includes('employeeid')) {
          setDuplicateAlert({
            title: 'Employee ID Already Used',
            message: err.response?.data?.message || 'This employee ID has already been used. Please try with a different one.',
          });
        } else if (msg.includes('email')) {
          setDuplicateAlert({
            title: 'Email Already Used',
            message: err.response?.data?.message || 'This email address has already been used. Please try with a different one.',
          });
        } else {
          setDuplicateAlert({
            title: 'Facility / Access Code Already in Use',
            message: err.response?.data?.message || 'A token with this Facility Code and Access Code combination has already been generated. Please use a different Facility Code or Access Code.',
          });
        }
      } else if (err.response?.status === 403 || err.response?.data?.limitReached) {
        setLimitReached(true);
        fetchQuota();
      } else {
        setErrorMsg(err.response?.data?.message || 'Failed to save user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Extend-validity handlers ────────────────────────────────────────────────
  const handleEditClick = (id) => {
    setEditingTokenId(id);
    setExtendError('');
  };
  const handleExtendCancel = () => {
    setEditingTokenId(null);
    setExtendError('');
  };
  const handleExtendSubmit = async (id, validFrom, validUntil) => {
    setExtending(true);
    setExtendError('');
    try {
      const res = await API.patch(`${apiBase}/history/${id}/extend-validity`, {
        validFrom: new Date(validFrom).toISOString(),
        validUntil: new Date(validUntil).toISOString(),
      });
      setHistory((h) => h.map((t) => (t._id === id ? { ...t, ...res.data.token } : t)));
      setEditingTokenId(null);
    } catch (err) {
      setExtendError(err.response?.data?.message || 'Failed to extend validity.');
    } finally {
      setExtending(false);
    }
  };

  // ── Card deactivate / block handlers ────────────────────────────────────────
  const handleToggleClick = () => {
    if (!viewingCard?.claimId) return; // nothing to deactivate
    setShowDeactivateConfirm(true);
  };

  const handleDeactivateCard = async () => {
    if (!viewingCard) return;
    setDeactivating(true);
    setDeactivateError('');
    try {
      const res = await API.patch(`${apiBase}/history/${viewingCard._id}/reset-claim`);
      setHistory((h) => h.map((t) => (t._id === viewingCard._id ? { ...t, ...res.data.token } : t)));
      setViewingCard((v) => (v ? { ...v, ...res.data.token } : v));
      setDeactivateSuccess(true);
      setShowDeactivateConfirm(false);
    } catch (err) {
      setDeactivateError(err.response?.data?.message || 'Failed to deactivate card.');
      setShowDeactivateConfirm(false);
    } finally {
      setDeactivating(false);
    }
  };

  const handleBlockUser = async (type, reason) => {
    if (!viewingCard) return;
    setBlocking(true);
    setBlockError('');
    try {
      const res = await API.post(`${apiBase}/history/${viewingCard._id}/block`, { type, reason });
      const patch = res.data.token;
      setHistory((h) => h.map((t) => (t._id === viewingCard._id ? { ...t, ...patch } : t)));
      setViewingCard((v) => (v ? { ...v, ...patch } : v));
      setShowBlockModal(false);
      loadBlocklist();
    } catch (err) {
      setBlockError(err.response?.data?.message || 'Failed to update this user.');
    } finally {
      setBlocking(false);
    }
  };

  const closeViewingCard = () => {
    setViewingCard(null);
    setDeactivateError('');
    setDeactivateSuccess(false);
  };

  // ── Quota-derived flag ──────────────────────────────────────────────────────
  const isLimitBlocked = quota.tokenLimit !== null &&
    quota.tokenLimit !== undefined &&
    quota.tokensUsed >= quota.tokenLimit;

  // ── Date filter helpers ─────────────────────────────────────────────────────
  const isSameDay = (isoString, dateStr) => {
    if (!isoString || !dateStr) return false;
    const d = new Date(isoString);
    const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return local === dateStr;
  };

  // ── Active source list: date-filtered / searched, excluding blocked users ──
  const sourceTokens = (selectedDate
    ? history.filter((t) => isSameDay(t.sentAt, selectedDate))
    : history
  )
    .filter((t) => !t.blockStatus)
    .filter((t) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.trim().toLowerCase();
      const name = `${t.firstName || ''} ${t.lastName || ''}`.toLowerCase();
      return name.includes(q) || (t.employeeId || '').toLowerCase().includes(q);
    });

  // ── Shared pagination over whichever source list is active ────────────────
  const totalPages = Math.max(1, Math.ceil(sourceTokens.length / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pagedTokens = sourceTokens.slice(startIndex, startIndex + PAGE_SIZE);

  // Group only the current page's tokens by month
  const groupByMonth = (tokens) => {
    const groups = {};
    tokens.forEach((t) => {
      const d = t.sentAt ? new Date(t.sentAt) : new Date();
      const key = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  };
  const groupedPageTokens = groupByMonth(pagedTokens);

  const handlePageChange = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  const handleCalendarSelect = (isoDate) => {
    setSelectedDate(isoDate);
    setPage(1);
    setCalendarOpen(false);
  };

  const clearDate = () => {
    setSelectedDate('');
    setPage(1);
    setCalendarOpen(false);
  };

  const selectedCountry = COUNTRY_CODES.find((c) => c.dial === form.countryCode);

  const renderRow = (t) => (
    <HistoryRow
      key={t._id}
      token={t}
      isEditing={editingTokenId === t._id}
      onEditClick={() => handleEditClick(t._id)}
      onExtendSubmit={handleExtendSubmit}
      onExtendCancel={handleExtendCancel}
      onView={() => setViewingCard(t)}
      extending={extending}
      extendError={editingTokenId === t._id ? extendError : ''}
    />
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="admin-page">

      {/* Duplicate alert — Facility/Access Code, Email, Employee ID, or blocklist */}
      {duplicateAlert && (
        <DuplicateAlertModal
          title={duplicateAlert.title}
          message={duplicateAlert.message}
          onClose={() => setDuplicateAlert(null)}
        />
      )}

      {showDeactivateConfirm && (
        <DeactivateConfirmModal
          onConfirm={handleDeactivateCard}
          onCancel={() => setShowDeactivateConfirm(false)}
          loading={deactivating}
        />
      )}

      {showBlockModal && viewingCard && (
        <BlockUserModal
          user={viewingCard}
          onConfirm={handleBlockUser}
          onCancel={() => { if (!blocking) setShowBlockModal(false); }}
          loading={blocking}
          error={blockError}
        />
      )}

      {/* Page header */}
      <div className="admin-page-header">
        <div className="admin-page-header-icon" style={{ background: accentColor, color: '#1565c0' }}>
          <IconKey size={22} color="#1565c0" />
        </div>
        <div>
          <h1 className="admin-page-title">Token Generation</h1>
          <p className="admin-page-subtitle">
            Save an employee's details here. They sign in from the mobile app with their registered email and a one-time code requested from the app.
          </p>
        </div>
      </div>

      <div className="tg-layout">

        {/* ── Left: Form card ── */}
        <div className="tg-card">
          <h2 className="tg-card-title">{viewingCard ? 'Card Details' : 'Add User'}</h2>

          {/* Quota bar — only for vendor/user */}
          <QuotaBar used={quota.tokensUsed} limit={quota.tokenLimit} />

          {viewingCard && (
            <CardDetailsPanel
              card={viewingCard}
              deactivating={deactivating}
              deactivateError={deactivateError}
              justDeactivated={deactivateSuccess}
              onDeactivate={handleToggleClick}
              onBlock={() => { setBlockError(''); setShowBlockModal(true); }}
              onClose={closeViewingCard}
            />
          )}

          {/* Bulk Excel import — above the single-user form */}
          <ExcelImportPanel
            importEndpoint={apiBase}
            onComplete={() => {
              loadHistory().then(() => setPage(1));
              fetchQuota();
            }}
          />

          <form onSubmit={handleSubmit} noValidate>
            <div className="tg-field">
              <label className="tg-label">Client Name</label>
              <input type="text" name="clientName"
                value={form.clientName} onChange={handleTextInput}
                placeholder="Enter client name"
                className={`tg-input ${errors.clientName ? 'tg-input--error' : ''}`} autoComplete="off"
                disabled={isLimitBlocked} />
              {errors.clientName && <p className="tg-error">{errors.clientName}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">
                Facility Code <span className="tg-tag">max 65535</span>
              </label>
              <input type="text" inputMode="numeric" name="facilityCode"
                value={form.facilityCode} onChange={handleNumberInput}
                placeholder="Enter facility code (0 – 65535)" maxLength={5}
                className={`tg-input ${errors.facilityCode ? 'tg-input--error' : ''}`} autoComplete="off"
                disabled={isLimitBlocked} />
              {errors.facilityCode && <p className="tg-error">{errors.facilityCode}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">
                Access Code <span className="tg-tag">max 16,777,215</span>
              </label>
              <input type="text" inputMode="numeric" name="accessCode"
                value={form.accessCode} onChange={handleNumberInput}
                placeholder="Enter access code (0 – 16,777,215)" maxLength={8}
                className={`tg-input ${errors.accessCode ? 'tg-input--error' : ''}`} autoComplete="off"
                disabled={isLimitBlocked} />
              {errors.accessCode && <p className="tg-error">{errors.accessCode}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">Employee ID</label>
              <input type="text" name="employeeId"
                value={form.employeeId} onChange={handleTextInput}
                placeholder="Enter employee ID"
                className={`tg-input ${errors.employeeId ? 'tg-input--error' : ''}`} autoComplete="off"
                disabled={isLimitBlocked} />
              {errors.employeeId && <p className="tg-error">{errors.employeeId}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">First Name</label>
              <input type="text" name="firstName"
                value={form.firstName} onChange={handleTextInput}
                placeholder="Enter first name"
                className={`tg-input ${errors.firstName ? 'tg-input--error' : ''}`} autoComplete="off"
                disabled={isLimitBlocked} />
              {errors.firstName && <p className="tg-error">{errors.firstName}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">Last Name</label>
              <input type="text" name="lastName"
                value={form.lastName} onChange={handleTextInput}
                placeholder="Enter last name"
                className={`tg-input ${errors.lastName ? 'tg-input--error' : ''}`} autoComplete="off"
                disabled={isLimitBlocked} />
              {errors.lastName && <p className="tg-error">{errors.lastName}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">Employee Email</label>
              <input type="email" name="employeeEmail"
                value={form.employeeEmail} onChange={handleEmailInput}
                placeholder="employee@company.com"
                className={`tg-input ${errors.employeeEmail ? 'tg-input--error' : ''}`} autoComplete="off"
                disabled={isLimitBlocked} />
              {errors.employeeEmail && <p className="tg-error">{errors.employeeEmail}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">Phone Number <span className="tg-tag">optional</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={form.countryCode}
                  onChange={handleCountryCodeChange}
                  className="tg-input"
                  style={{ flex: '0 0 118px' }}
                  disabled={isLimitBlocked}
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.iso} value={c.dial}>{c.dial} {c.iso}</option>
                  ))}
                </select>
                <input
                  type="text" inputMode="numeric" name="phoneNumber"
                  value={form.phoneNumber} onChange={handlePhoneInput}
                  placeholder={selectedCountry ? `${selectedCountry.length}-digit number` : 'Phone number'}
                  maxLength={selectedCountry?.length || 15}
                  className={`tg-input ${errors.phoneNumber ? 'tg-input--error' : ''}`}
                  style={{ flex: 1 }} autoComplete="off"
                  disabled={isLimitBlocked}
                />
              </div>
              {errors.phoneNumber && <p className="tg-error">{errors.phoneNumber}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">Valid From</label>
              <input type="datetime-local" name="validFrom"
                value={form.validFrom} onChange={handleValidFromChange}
                className={`tg-input ${errors.validFrom ? 'tg-input--error' : ''}`}
                disabled={isLimitBlocked} />
              {errors.validFrom && <p className="tg-error">{errors.validFrom}</p>}
            </div>

            <div className="tg-field">
              <label className="tg-label">Valid For</label>
              <div className="tg-duration-presets">
                {DURATION_PRESETS.map((p) => (
                  <button
                    type="button"
                    key={p.key}
                    className={`tg-duration-btn ${durationPreset === p.key ? 'tg-duration-btn--active' : ''}`}
                    onClick={() => handleDurationPreset(p.key)}
                    disabled={isLimitBlocked}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`tg-duration-btn ${durationPreset === 'custom' ? 'tg-duration-btn--active' : ''}`}
                  onClick={() => setDurationPreset('custom')}
                  disabled={isLimitBlocked}
                >
                  Custom
                </button>
              </div>
            </div>

            <div className="tg-field">
              <label className="tg-label">Valid To</label>
              <input type="datetime-local" name="validUntil"
                value={form.validUntil} onChange={handleValidUntilChange}
                disabled={durationPreset !== 'custom' || isLimitBlocked}
                className={`tg-input ${errors.validUntil ? 'tg-input--error' : ''}`} />
              {errors.validUntil && <p className="tg-error">{errors.validUntil}</p>}
            </div>

            <button type="submit" className="tg-btn" disabled={loading || isLimitBlocked}
              style={isLimitBlocked ? { background: '#9ca3af', cursor: 'not-allowed' } : {}}>
              {loading
                ? <><span className="tg-spinner" /> Saving User...</>
                : isLimitBlocked
                  ? <>Token Limit Reached</>
                  : <><UserPlusIcon /> Save User</>}
            </button>
          </form>

          {/* Limit reached banner */}
          {(limitReached || isLimitBlocked) && <LimitReachedBanner role={role} />}

          {/* Success block — no email is sent */}
          {successEmail && !limitReached && (
            <div className="tg-success-block" style={{ marginTop: '1rem' }}>
              <div className="tg-success-top">
                <span className="tg-success-check">✓</span>
                <div>
                  <p className="tg-success-title">User saved successfully</p>
                  <p className="tg-success-sub">
                    <strong>{successEmail}</strong> can now sign in from the mobile app using Get OTP.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error block — validation OR API error */}
          {errorMsg && !limitReached && (
            <div className="tg-banner tg-banner--error" style={{ marginTop: '1rem' }}>
              <span className="tg-banner-icon">✕</span>
              <div>
                <p className="tg-banner-title">
                  {Object.keys(errors).length > 0 ? 'Missing information' : 'Failed to save user'}
                </p>
                <p className="tg-banner-sub">{errorMsg}</p>
              </div>
            </div>
          )}

          <div className="tg-info">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Saving a user does not send any email. The user requests a one-time code from the mobile app (Get OTP), valid for 5 minutes. Card tokens are AES-256 encrypted and all records are saved in the <strong>{role}</strong> collection for audit.
          </div>
        </div>

        {/* ── Right: History panel ── */}
        <div className="tg-history">
          <div className="tg-history-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 className="tg-card-title" style={{ margin: 0 }}>
                {showBlocklist ? 'Resigned / Blocklisted' : 'Saved Users'}
              </h2>
              <span className="tg-history-count">
                {showBlocklist ? blocklist.length : history.filter((t) => !t.blockStatus).length} records
              </span>

              <input
                type="text"
                className="tg-input"
                style={{ width: 200 }}
                placeholder="Search name or emp ID"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              />
            </div>

            <div className="tg-history-header-actions">

              {showBlocklist ? (
                <button className="tg-hbtn tg-hbtn--neutral" onClick={() => setShowBlocklist(false)}>
                  ← Back to history
                </button>
              ) : (
                <>
                  <button
                    className="tg-hbtn tg-hbtn--danger"
                    onClick={openBlocklist}
                    title="View resigned and blocklisted users"
                  >
                    <BlockUserIcon /> Resigned ({blocklist.length})
                  </button>

                  {/* Custom calendar dropdown */}
                  <div className="tg-calendar-wrap">
                    <button
                      type="button"
                      className="tg-hbtn tg-hbtn--neutral"
                      onClick={() => setCalendarOpen((o) => !o)}
                    >
                      <CalendarIcon />
                      {selectedDate
                        ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'Pick a date'}
                    </button>

                    {calendarOpen && (
                      <CalendarDropdown
                        selectedDate={selectedDate}
                        onSelect={handleCalendarSelect}
                        onClear={clearDate}
                        onClose={() => setCalendarOpen(false)}
                      />
                    )}
                  </div>

                  {selectedDate && (
                    <button className="tg-hbtn tg-hbtn--neutral" onClick={clearDate}>
                      Clear
                    </button>
                  )}

                  {history.length > 0 && (
                    <button
                      className="tg-hbtn tg-hbtn--success"
                      onClick={handleExportHistory}
                      title="Export all token history to Excel"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Export Excel
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* List: blocklist / loading / empty / paginated (grouped by month within the page) */}
          {showBlocklist ? (
            <BlocklistPanel
              users={blocklist}
              loading={blocklistLoading}
              tab={blockTab}
              onTabChange={setBlockTab}
              query={searchQuery}
            />
          ) : histLoading ? (
            <div className="tg-history-empty"><p>Loading history...</p></div>
          ) : history.length === 0 ? (
            <div className="tg-history-empty">
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>📭</div>
              <p>No users saved yet.</p>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 4 }}>
                Users you save will appear here.
              </p>
            </div>
          ) : sourceTokens.length === 0 ? (
            <div className="tg-history-empty">
              <p>No users found for this search or date.</p>
            </div>
          ) : (
            <>
              <div className="tg-history-list">
                {selectedDate && (
                  <p className="tg-history-group-label">
                    {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                    {' '}({sourceTokens.length} record{sourceTokens.length === 1 ? '' : 's'})
                  </p>
                )}

                {selectedDate
                  ? pagedTokens.map(renderRow)
                  : Object.entries(groupedPageTokens).map(([monthLabel, tokens]) => (
                    <div key={monthLabel} className="tg-history-group">
                      <p className="tg-history-group-label">{monthLabel}</p>
                      {tokens.map(renderRow)}
                    </div>
                  ))}
              </div>

              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default TokenPage;